import { Home, Search, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useReveal } from "@/hooks/useReveal";

const AudienceSection = () => {
  const header = useReveal<HTMLDivElement>();
  const left = useReveal<HTMLDivElement>(0.2);
  const right = useReveal<HTMLDivElement>(0.2);

  return (
    <section className="pt-8 pb-28 md:pt-16 md:pb-36 px-8 md:px-12 bg-canvas">
      <div className="max-w-[960px] mx-auto">
        <div
          ref={header.ref}
          className={`max-w-2xl mb-20 scroll-reveal ${header.visible ? "is-visible" : ""}`}
        >
          <div className="inline-flex items-center gap-2.5 mb-6">
            <span className="w-8 h-px bg-gold-deep" />
            <span className="text-[11px] font-medium tracking-[0.25em] uppercase text-ink-soft">
              Who it's for
            </span>
          </div>
          <h2 className="font-display text-5xl md:text-7xl font-light text-ink leading-[1.0] tracking-tight mb-6">
            Selling or buying —
            <br />
            <span className="accent-italic text-wood-deep">we're with you.</span>
          </h2>
          <p className="text-base md:text-lg text-ink-soft leading-[1.7] font-light max-w-xl">
            You shouldn't have to choose between getting help and keeping your money. Real support through every step — without the commission that eats your equity.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Seller Card — charcoal, gold accent */}
          <div
            ref={left.ref}
            className={`group relative overflow-hidden bg-ink text-canvas rounded-[20px] p-10 md:p-12 card-lift scroll-reveal-left ${left.visible ? "is-visible" : ""}`}
            style={{ transitionDelay: "0.05s" }}
          >
            <div className="absolute -top-32 -right-32 w-80 h-80 rounded-full bg-gold/15 blur-3xl group-hover:bg-gold/25 transition-colors duration-1000 animate-drift" />
            <div className="relative">
              <div className="w-12 h-12 rounded-full border border-canvas/20 flex items-center justify-center mb-8">
                <Home className="h-4.5 w-4.5 text-gold" strokeWidth={1.5} />
              </div>
              <p className="font-display text-4xl md:text-5xl font-light text-canvas mb-4 leading-[1.05] tracking-tight">I want to <span className="accent-italic text-gold">sell</span> my home</p>
              <p className="text-sm text-canvas/65 leading-[1.7] font-light mb-10 max-w-md">
                We walk beside you from listing to closing — pricing, photography, showings, offers, negotiation. The only thing missing is the commission.
              </p>

              <div className="pt-6 border-t border-canvas/10 mb-8">
                <p className="font-display text-5xl font-light text-gold mb-2 tabular-nums">$12k–$30k</p>
                <p className="text-[11px] text-canvas/55 uppercase tracking-[0.2em]">stays in your pocket</p>
              </div>

              <Link to="/list-property">
                <Button className="group/btn bg-gold hover:bg-gold-deep text-ink font-medium px-7 py-6 text-sm tracking-wide rounded-md transition-all duration-500">
                  List my home
                  <ArrowUpRight className="ml-2 w-4 h-4 transition-transform duration-500 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Buyer Card — linen, charcoal text */}
          <div
            ref={right.ref}
            className={`group relative overflow-hidden bg-linen text-ink rounded-[20px] p-10 md:p-12 card-lift scroll-reveal-right ${right.visible ? "is-visible" : ""}`}
            style={{ transitionDelay: "0.18s" }}
          >
            <div className="absolute -bottom-32 -left-32 w-80 h-80 rounded-full bg-wood/20 blur-3xl group-hover:bg-wood/30 transition-colors duration-1000 animate-drift" />
            <div className="relative">
              <div className="w-12 h-12 rounded-full border border-ink/15 flex items-center justify-center mb-8 bg-canvas">
                <Search className="h-4.5 w-4.5 text-ink" strokeWidth={1.5} />
              </div>
              <p className="font-display text-4xl md:text-5xl font-light text-ink mb-4 leading-[1.05] tracking-tight">I want to <span className="accent-italic text-wood-deep">find</span> my home</p>
              <p className="text-sm text-ink-soft leading-[1.7] font-light mb-10 max-w-md">
                Search, schedule tours, compare prices, and negotiate with confidence. Real guidance at every turn — without paying a buyer's agent 3%.
              </p>

              <div className="pt-6 border-t border-ink/10 mb-8">
                <p className="font-display text-5xl font-light text-ink mb-2 tabular-nums">3% saved</p>
                <p className="text-[11px] text-ink-soft uppercase tracking-[0.2em]">support ≠ a commissioned agent</p>
              </div>

              <Link to="/listings">
                <Button className="group/btn bg-ink hover:bg-ink/90 text-canvas font-medium px-7 py-6 text-sm tracking-wide rounded-md transition-all duration-500">
                  Browse homes
                  <ArrowUpRight className="ml-2 w-4 h-4 transition-transform duration-500 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AudienceSection;
